import { useState } from 'react';
import {
  KeyboardArrowDown as ArrowDownIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  AccountCircle as ProfileIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type { User } from '@core/api/user';
import { ActionButton } from '@core/components';
import { ROLE_LABELS } from '@core/enum/userRole';
import { useTenantSession } from '@core/hooks/useTenantSession';
import TenantSwitchMenuItem from './TenantSwitchMenuItem';

interface UserMenuProps {
  variant?: 'appbar' | 'sidebar';
  user?: User;
  onLogout?: () => void;
  onProfileClick?: () => void;
  onChangePasswordClick?: () => void;
  onThemeToggle?: () => void;
  isDarkMode?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({
  variant = 'appbar',
  user,
  onLogout,
  onProfileClick,
  onChangePasswordClick,
  onThemeToggle,
  isDarkMode = false,
}) => {
  const { tenant, canSwitch, selectTenant } = useTenantSession();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMinWidth, setMenuMinWidth] = useState<number>();
  const open = Boolean(anchorEl);
  const isSidebar = variant === 'sidebar';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuMinWidth(event.currentTarget.offsetWidth);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    }
    return user?.email?.charAt(0) || 'U';
  };

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email || 'User';
  };

  const handleMenuItemClick = (callback?: () => void) => {
    handleClose();
    callback?.();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: isSidebar ? '100%' : 'auto',
      }}
    >
      <ActionButton
        variant="ghost"
        onClick={handleClick}
        sx={{
          gap: 1,
          justifyContent: 'space-between',
          width: isSidebar ? '100%' : 'auto',
          minWidth: isSidebar ? 0 : { xs: 'auto', md: 225 },
          px: isSidebar ? 1 : undefined,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            src={user?.profilePicture || undefined}
            alt={`${user?.firstName} ${user?.lastName}`}
          >
            {getUserInitials()}
          </Avatar>
          <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left' }}>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              {getUserDisplayName()}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {user?.role ? ROLE_LABELS[user.role] : null}
            </Typography>
          </Box>
        </Box>

        <ArrowDownIcon
          fontSize="small"
          sx={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      </ActionButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: isSidebar ? -1 : 1,
            minWidth: {
              xs: 200,
              md: menuMinWidth,
            },
            borderRadius: 2,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          },
        }}
        anchorOrigin={{
          vertical: isSidebar ? 'top' : 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: isSidebar ? 'bottom' : 'top',
          horizontal: 'center',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {getUserDisplayName()}
          </Typography>
          {user?.email && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {user.email}
            </Typography>
          )}
        </Box>
        <MenuItem onClick={() => handleMenuItemClick(onProfileClick)}>
          <ListItemIcon>
            <ProfileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(onChangePasswordClick)}>
          <ListItemIcon>
            <LockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change password</ListItemText>
        </MenuItem>
        {canSwitch ? (
          <TenantSwitchMenuItem
            tenant={tenant}
            onSelect={selectTenant}
            onDone={handleClose}
          />
        ) : null}
        <MenuItem onClick={() => handleMenuItemClick(onThemeToggle)}>
          <ListItemIcon>
            {isDarkMode ? (
              <LightModeIcon fontSize="small" />
            ) : (
              <DarkModeIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</ListItemText>
        </MenuItem>
        {onLogout && (
          <MenuItem onClick={() => handleMenuItemClick(onLogout)}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default UserMenu;
